import React, { useState } from 'react'
import { User, Lock, Loader2, Check, X, CreditCard } from 'lucide-react'
import { toast } from 'sonner'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { useUserProfile } from '../context/UserProfileContext'
import { ProfileService } from '../utils/profileService'
import { useSubscription } from '../hooks/useSubscription'
import { useNavigate } from 'react-router-dom'

export default function SettingsPage() {
  const { user, refetchUser } = useUserProfile()
  const { subscriptionData, hasActiveSubscription } = useSubscription()
  const navigate = useNavigate()
  
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  
  // Profile form state
  const [firstName, setFirstName] = useState(user?.firstName || '')
  const [lastName, setLastName] = useState(user?.lastName || '')
  const [email, setEmail] = useState(user?.email || '')
  const [phoneNumber, setPhoneNumber] = useState(() => {
    const raw = user?.phoneNumber || ''
    return raw.startsWith('+44') ? raw.slice(3) : raw
  })
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  
  // Notifications state (commented out for future use)
  // const [emailNotifications, setEmailNotifications] = useState(true)
  // const [deadlineReminders, setDeadlineReminders] = useState(true)

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    
    try {
      setIsUpdatingProfile(true)
      await ProfileService.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        ...(phoneNumber.trim() ? { phoneNumber: `+44${phoneNumber.replace(/\s/g, '').trim()}` } : {}),
      })
      
      toast.success('Profile updated successfully')
      await refetchUser()
    } catch (error: any) {
      console.error('Failed to update profile:', error)
      toast.error(error?.message || 'Failed to update profile')
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields')
      return
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long')
      return
    }
    
    try {
      setIsChangingPassword(true)
      await ProfileService.changePassword({
        currentPassword,
        newPassword,
      })
      
      toast.success('Password changed successfully')
      setShowPasswordModal(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: any) {
      console.error('Failed to change password:', error)
      toast.error(error?.message || 'Failed to change password')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatCurrency = (amount?: number, currency: string = 'GBP') => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount)
  }

  return (
    <AuthenticatedLayout activeTab='settings'>
      <div className='grid grid-cols-1 gap-6'>
        {/* Subscription Status Card */}
        {hasActiveSubscription && subscriptionData?.subscription && (
          <div className='p-4 sm:p-6 bg-gradient-to-r from-red-50 to-red-100 rounded-2xl shadow-sm border border-red-200'>
            <div className='flex flex-col sm:flex-row items-start justify-between gap-3'>
              <div className='flex items-start gap-3'>
                <div className='p-2 bg-white rounded-lg'>
                  <CreditCard className='w-6 h-6 text-red-600' />
                </div>
                <div>
                  <h3 className='text-lg font-bold text-gray-900 mb-1'>Active Subscription</h3>
                  <p className='text-sm text-gray-700'>
                    {subscriptionData.subscription.plan.name} - {subscriptionData.subscription.plan.productType}
                  </p>
                  <div className='mt-2 flex items-center gap-2'>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      subscriptionData.subscription.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscriptionData.subscription.status}
                    </span>
                    {subscriptionData.subscription.isExpiringSoon && (
                      <span className='text-xs text-amber-600'>
                        Expires in {subscriptionData.subscription.daysUntilExpiry} days
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className='text-sm text-red-600 hover:text-red-700 font-medium'
              >
                View Plans
              </button>
            </div>
            
            {subscriptionData.lastPayment && (
              <div className='mt-4 pt-4 border-t border-red-200'>
                <div className='text-sm text-gray-700'>
                  Last payment: {formatCurrency(subscriptionData.lastPayment.amount, subscriptionData.lastPayment.currency)} 
                  <span className='mx-2'>•</span>
                  {formatDate(subscriptionData.lastPayment.date)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile Settings */}
        <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
          <div className='flex items-start gap-2 mb-6'>
            <User className='w-5 h-5 text-gray-700 mt-1' />
            <h3 className='text-lg font-bold text-gray-900'>Profile Settings</h3>
          </div>

          <form onSubmit={handleUpdateProfile} className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  First Name
                </label>
                <input
                  type='text'
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className='w-full px-4 py-2 text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Last Name
                </label>
                <input
                  type='text'
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className='w-full px-4 py-2 border text-black border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500'
                  required
                />
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Email
                </label>
                <input
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className='w-full px-4 py-2 border text-black border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Phone Number
                </label>
                <div className='flex'>
                  <div className='flex items-center gap-1.5 px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 shrink-0'>
                    <span className='text-sm'>🇬🇧</span>
                    <span className='text-sm font-medium text-gray-600'>+44</span>
                  </div>
                  <input
                    type='tel'
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d\s]/g, ''))}
                    placeholder='7700 900000'
                    className='w-full px-4 py-2 border text-black border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-red-500'
                  />
                </div>
              </div>
            </div>

            <div className='flex items-center gap-3 pt-2'>
              <button
                type='submit'
                disabled={isUpdatingProfile}
                className='bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2'
              >
                {isUpdatingProfile ? (
                  <>
                    <Loader2 className='w-4 h-4 animate-spin' />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className='w-4 h-4' />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Additional Settings */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 w-full'>
          {/* Security Settings */}
          <div className='p-6 bg-white rounded-2xl shadow-sm border-0 w-full'>
            <div className='flex items-start gap-2 mb-6'>
              <Lock className='w-5 h-5 text-gray-700 mt-1' />
              <h3 className='text-lg font-bold text-gray-900'>Security</h3>
            </div>

            <div className='space-y-4'>
              <button
                onClick={() => setShowPasswordModal(true)}
                className='w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'
              >
                <div className='font-medium text-gray-900'>Change Password</div>
                <div className='text-sm text-gray-600'>
                  Update your password regularly
                </div>
              </button>
            </div>
          </div>

          {/* Notifications */}
          {/* <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
            <div className='flex items-start gap-2 mb-6'>
              <Bell className='w-5 h-5 text-gray-700 mt-1' />
              <h3 className='text-lg font-bold text-gray-900'>Notifications</h3>
            </div>

            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <div className='font-medium text-gray-900'>Email Notifications</div>
                  <div className='text-sm text-gray-600'>
                    Receive updates via email
                  </div>
                </div>
                <label className='relative inline-flex items-center cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    className='sr-only peer'
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>

              <div className='flex items-center justify-between'>
                <div>
                  <div className='font-medium text-gray-900'>Deadline Reminders</div>
                  <div className='text-sm text-gray-600'>
                    Get reminders for Recently Added Companies
                  </div>
                </div>
                <label className='relative inline-flex items-center cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={deadlineReminders}
                    onChange={(e) => setDeadlineReminders(e.target.checked)}
                    className='sr-only peer'
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className='fixed inset-0 bg-gray-500/50 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-2xl shadow-xl max-w-md w-full p-6'>
            <div className='flex items-center justify-between mb-6'>
              <h3 className='text-xl font-bold text-gray-900'>Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className='text-gray-400 hover:text-gray-600'
              >
                <X className='w-5 h-5' />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Current Password
                </label>
                <input
                  type='password'
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500'
                  required
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  New Password
                </label>
                <input
                  type='password'
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500'
                  required
                  minLength={8}
                />
                <p className='text-xs text-gray-500 mt-1'>
                  Must be at least 8 characters long
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Confirm New Password
                </label>
                <input
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500'
                  required
                />
              </div>

              <div className='flex items-center gap-3 pt-4'>
                <button
                  type='submit'
                  disabled={isChangingPassword}
                  className='flex-1 bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
                >
                  {isChangingPassword ? (
                    <>
                      <Loader2 className='w-4 h-4 animate-spin' />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
                <button
                  type='button'
                  onClick={() => setShowPasswordModal(false)}
                  className='px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AuthenticatedLayout>
  )
}
